import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    useLocation,
    useNavigate,
    Navigate,
    useParams,
} from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import Button from 'components/v4/Button';
import FormLabel from 'components/v4/FormLabel';
import DropDown from 'components/forms/new/formikFormFields/DropDown';
import { multiSelectDataMapper } from 'utils/utils';
import Heading from 'components/v4/Heading';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import LoadingIndicator from 'components/v4/LoadingIndicator';

import useFieldValidation from 'hooks/useFieldValidation';
import FieldValidation from 'components/v4/FieldValidation';

import { axios } from '@/services/axios';
import style from 'pages/services/serviceprofiles/edit/styles';
import FooterContainer from 'pages/services/serviceprofiles/edit/FooterContainer';
import { validationSchemaOCC } from 'pages/services/serviceprofiles/edit/schema';
import { useQuery } from 'hooks/useCustomQuery';

const DEFAULT_OCC_PAYLOAD = {
    name: 'Operator Connect Conferencing',
    callingProfiles: [],
};

export default function OCCEditService() {
    const { state: data } = useLocation();
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { id } = useParams();

    const [callingProfiles, setCallingProfiles] = useState([]);

    const { currentCompany: companyID } = useSelector(
        (state) => state.navigationLists,
    );

    const { darkMode } = useSelector((state) => state.settings);

    const [modalState, setModalState] = useState(data || DEFAULT_OCC_PAYLOAD);
    const { validationErrors, validatefn } = useFieldValidation(
        modalState,
        validationSchemaOCC,
    );

    const serviceId = modalState?.id || id;

    const { isLoading: isInitialLoading } = useQuery({
        queryKey: ['occEditServiceData', serviceId, companyID],
        queryFn: async () => {
            const result = await axios.get(`/service/${serviceId}`);
            const mappedCallingProfiles = result.data?.callingProfiles?.map(
                multiSelectDataMapper,
            );
            const cpResult = await axios.get(
                `/Services/OperatorConnectConferencing/Company/${companyID}/Profile/${result.data.operatorProfileID}/CallingProfiles/Available`,
            );

            return {
                serviceData: {
                    ...result.data,
                    callingProfiles: mappedCallingProfiles,
                },
                callingProfiles: cpResult.data.map(multiSelectDataMapper),
            };
        },
        enabled: Boolean(serviceId) && Boolean(companyID),
        onSuccess: ({ serviceData, callingProfiles: cpOptions }) => {
            setModalState(serviceData);
            setCallingProfiles(cpOptions);
        },
        onError: (e) => {
            setError(e);
        },
        refetchOnWindowFocus: false,
    });

    const handleSubmit = async () => {
        setError(null);
        //validate
        if (!validatefn()) {
            setError({
                response: {
                    data: {
                        message:
                            'There are some validation errors in the form. Please check and try again.',
                    },
                },
            });
            return;
        }
        setIsSubmitting(true);
        const payload = {
            ...modalState,
            companyID,
        };
        await axios
            .put('/Service/OperatorConnectConferencing', payload)
            .then(() => {
                handleBackButton();
            })
            .catch((e) => setError(e))
            .finally(() => setIsSubmitting(false));
    };

    // function to set modal state with a given payload
    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    const handleBackButton = () => {
        navigate('/dashboard-services');
    };

    if (!id) {
        return <Navigate to="/dashboard-services" />;
    }

    if (isInitialLoading || isSubmitting) {
        return <LoadingIndicator />;
    }

    return (
        <Box sx={{ maxWidth: '2xl', minWidth: 'xl', margin: 'auto' }}>
            <Heading fontSize="3xl" as="h2" sx={{ marginBottom: '10px' }}>
                Edit {modalState.name}
            </Heading>

            {error && (
                <Alert status="error" sx={{ marginTop: '1rem' }}>
                    <AlertIcon />
                    <AlertDescription>
                        {error?.response?.data?.message ||
                            'An error occured. Please try again later.'}
                    </AlertDescription>
                </Alert>
            )}
            {modalState?.callingProfiles && callingProfiles && (
                <Box sx={style.fieldContainer}>
                    <FormLabel sx={style.label} pt={4}>
                        {' '}
                        Calling Profiles{' '}
                    </FormLabel>
                    <DropDown
                        data={callingProfiles}
                        shownValue="name"
                        isMulti
                        multiValue={
                            Array.isArray(modalState.callingProfiles)
                                ? modalState.callingProfiles.map((cp) => cp.id)
                                : []
                        }
                        darkMode={darkMode}
                        placeholder="- Select Calling Profiles -"
                        handleSelectChange={(ids) => {
                            const selected = (ids || [])
                                .map((id) =>
                                    (callingProfiles || []).find(
                                        (opt) => opt.id === id,
                                    ),
                                )
                                .filter(Boolean);
                            handleChangeState({ callingProfiles: selected });
                        }}
                    />
                    <FieldValidation
                        errors={validationErrors?.callingProfiles}
                    />
                </Box>
            )}

            <FooterContainer>
                <Button
                    variant="outline"
                    sx={{ background: 'white' }}
                    onClick={handleBackButton}>
                    Close
                </Button>
                <Button onClick={handleSubmit}>Update</Button>
            </FooterContainer>
        </Box>
    );
}
